import type { SVGProps } from "react";

type IconProps = SVGProps<SVGSVGElement> & { size?: number; filled?: boolean };

// 16-icon set per design_handoff_sytofood/shared.jsx Icon{}.
// Stroke 1.8, rounded line caps; "filled" toggles where applicable.

function Svg({ size = 22, children, ...rest }: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={1.8}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      {...rest}
    >
      {children}
    </svg>
  );
}

export const IconBack = (p: IconProps) => (
  <Svg {...p} strokeWidth={2}>
    <path d="M15 18l-6-6 6-6" />
  </Svg>
);
export const IconClose = (p: IconProps) => (
  <Svg {...p} strokeWidth={2}>
    <path d="M18 6L6 18M6 6l12 12" />
  </Svg>
);
export const IconPlus = (p: IconProps) => (
  <Svg {...p} strokeWidth={2.5}>
    <path d="M12 5v14M5 12h14" />
  </Svg>
);
export const IconSearch = (p: IconProps) => (
  <Svg {...p} strokeWidth={2}>
    <circle cx="11" cy="11" r="7" />
    <path d="M21 21l-4.3-4.3" />
  </Svg>
);
export const IconCamera = (p: IconProps) => (
  <Svg {...p}>
    <path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z" />
    <circle cx="12" cy="13" r="4" />
  </Svg>
);
export const IconMic = (p: IconProps) => (
  <Svg {...p}>
    <rect x="9" y="2" width="6" height="13" rx="3" />
    <path d="M5 11a7 7 0 0014 0M12 19v4" />
  </Svg>
);
export const IconBarcode = (p: IconProps) => (
  <Svg {...p}>
    <path d="M3 5v14M6 5v14M9 5v14M12 5v10M12 17v2M15 5v14M18 5v14M21 5v14" />
  </Svg>
);
export const IconKeyboard = (p: IconProps) => (
  <Svg {...p}>
    <rect x="2" y="6" width="20" height="13" rx="2" />
    <path d="M6 10h0M10 10h0M14 10h0M18 10h0M6 14h0M18 14h0M9 17h6" />
  </Svg>
);
export const IconHome = ({ filled, ...p }: IconProps) => (
  <Svg {...p} fill={filled ? "currentColor" : "none"}>
    <path d="M3 11l9-8 9 8M5 10v10h14V10" />
  </Svg>
);
export const IconFridge = ({ filled, ...p }: IconProps) => (
  <Svg {...p} fill={filled ? "currentColor" : "none"}>
    <rect x="5" y="2" width="14" height="20" rx="2.5" />
    <path d="M5 10h14M9 5v2M9 13v3" />
  </Svg>
);
export const IconBulb = ({ filled, ...p }: IconProps) => (
  <Svg {...p} fill={filled ? "currentColor" : "none"}>
    <path d="M9 18h6M10 22h4M12 2a6 6 0 00-4 10.5c1 1 1.5 2 1.5 3.5h5c0-1.5.5-2.5 1.5-3.5A6 6 0 0012 2z" />
  </Svg>
);
export const IconList = ({ filled, ...p }: IconProps) => (
  <Svg {...p}>
    <path d="M9 6h12M9 12h12M9 18h12" />
    <circle cx="4.5" cy="6" r="1.3" fill={filled ? "currentColor" : "none"} />
    <circle cx="4.5" cy="12" r="1.3" fill={filled ? "currentColor" : "none"} />
    <circle cx="4.5" cy="18" r="1.3" fill={filled ? "currentColor" : "none"} />
  </Svg>
);
export const IconUser = ({ filled, ...p }: IconProps) => (
  <Svg {...p}>
    <circle cx="12" cy="8" r="4" fill={filled ? "currentColor" : "none"} />
    <path d="M4 21c1-4 5-6 8-6s7 2 8 6" />
  </Svg>
);
export const IconStar = ({ filled, size = 18, ...p }: IconProps) => (
  <Svg size={size} {...p} fill={filled ? "currentColor" : "none"}>
    <path d="M12 2l3 7h7l-5.5 4.5L18 21l-6-4-6 4 1.5-7.5L2 9h7z" />
  </Svg>
);
export const IconCheck = ({ size = 18, ...p }: IconProps) => (
  <Svg size={size} {...p} strokeWidth={2.5}>
    <path d="M5 12l5 5L20 7" />
  </Svg>
);
export const IconEdit = ({ size = 16, ...p }: IconProps) => (
  <Svg size={size} {...p}>
    <path d="M11 4H5a2 2 0 00-2 2v13a2 2 0 002 2h13a2 2 0 002-2v-6M18 2l4 4-10 10H8v-4L18 2z" />
  </Svg>
);
export const IconTrash = ({ size = 16, ...p }: IconProps) => (
  <Svg size={size} {...p}>
    <path d="M3 6h18M8 6V4a2 2 0 012-2h4a2 2 0 012 2v2M19 6l-1 14a2 2 0 01-2 2H8a2 2 0 01-2-2L5 6" />
  </Svg>
);
export const IconSparkle = ({ size = 14, ...p }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...p}>
    <path d="M12 2l1.8 6.2L20 10l-6.2 1.8L12 18l-1.8-6.2L4 10l6.2-1.8z" />
    <path d="M19 14l.9 2.1 2.1.9-2.1.9L19 20l-.9-2.1-2.1-.9 2.1-.9z" />
  </svg>
);
export const IconChevR = ({ size = 14, ...p }: IconProps) => (
  <Svg size={size} {...p} strokeWidth={2}>
    <path d="M9 6l6 6-6 6" />
  </Svg>
);

// Meal-photo hint icons. Replace the legacy 💡 📐 🥑 emoji set; SVGs render
// at the same stroke weight as the rest of the icon system and stay legible
// at 14-16px in the chip strip.
export const IconSun = ({ filled, size = 16, ...p }: IconProps) => (
  <Svg size={size} {...p} fill={filled ? "currentColor" : "none"}>
    <circle cx="12" cy="12" r="4" />
    <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
  </Svg>
);
export const IconTopDown = ({ size = 16, ...p }: IconProps) => (
  <Svg size={size} {...p}>
    <path d="M12 3v14" />
    <path d="M6 13l6 6 6-6" />
    <path d="M4 22h16" />
  </Svg>
);
export const IconPlate = ({ size = 16, ...p }: IconProps) => (
  <Svg size={size} {...p}>
    <circle cx="12" cy="12" r="9" />
    <circle cx="12" cy="12" r="5.5" />
  </Svg>
);
export const IconGallery = ({ size = 18, ...p }: IconProps) => (
  <Svg size={size} {...p}>
    <rect x="3" y="3" width="18" height="18" rx="2.5" />
    <circle cx="8.5" cy="9" r="1.6" />
    <path d="M21 16l-5-5-9 9" />
  </Svg>
);
